import {
  IEmployeeActions,
  EmployeeContext,
  IEmployeeFetchActionPayload,
  IEmployeeNonPaginatedFetchActionPayload
} from './actions.types';
import {
  IEmployeeData,
  IEmployeeWorkScheduleData,
  IHrDepartmentData
} from '@/models';
import { IEmployeePaginatedListData } from '@/services';

export const actions: IEmployeeActions = {
  fetchSalaryEmployeeUsers(context: EmployeeContext): Promise<void> {
    return this.$services.employee.fetchSalaryEmployeeUsers().then(users => {
      context.commit('SET_EMPLOYEE_USERS', users);
    });
  },

  fetchSupervisorEmployees(
    context: EmployeeContext,
    payload: IEmployeeNonPaginatedFetchActionPayload
  ): Promise<IEmployeeData[]> {
    return this.$services.employee.fetchEmployees(payload).then(employees => {
      context.commit('SET_SUPERVISOR_EMPLOYEES', employees);
      return employees as IEmployeeData[];
    });
  },

  fetchEmployees(
    context: EmployeeContext,
    payload: IEmployeeFetchActionPayload
  ): Promise<IEmployeeData[]> {
    const promise = this.$services.employee.fetchEmployees(payload);
    context.commit('SET_LAST_PROMISE', promise);

    return promise.then(
      (employees: IEmployeePaginatedListData | IEmployeeData[]) => {
        if (promise == context.state.lastPromise) {
          if (payload && payload.page) {
            context.commit(
              'SET_EMPLOYEES',
              (employees as IEmployeePaginatedListData).results
            );
            context.commit(
              'SET_COUNT',
              (employees as IEmployeePaginatedListData).count
            );
          } else {
            context.commit('SET_EMPLOYEES', employees as IEmployeeData[]);
          }
          context.commit('SET_LAST_PROMISE', null);
        }
        if (payload && payload.page) {
          return (employees as IEmployeePaginatedListData).results;
        } else {
          return employees as IEmployeeData[];
        }
      }
    );
  },

  fetchEmployee(context: EmployeeContext, payload: number): Promise<void> {
    return this.$services.employee
      .fetchEmployee(payload)
      .then((employee: IEmployeeData) => {
        const index = context.state.employees.findIndex(
          (v: IEmployeeData) => v.id === employee.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE', employee);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_EMPLOYEE', employee);
        }
      });
  },

  createEmployee(
    context: EmployeeContext,
    payload: IEmployeeData
  ): Promise<number> {
    return this.$services.employee
      .createEmployee(payload)
      .then((employee: IEmployeeData) => {
        context.commit('ADD_EMPLOYEE', employee);
        context.commit('INCREMENT_COUNT');
        return employee.id!;
      });
  },

  updateEmployee(
    context: EmployeeContext,
    payload: IEmployeeData
  ): Promise<void> {
    return this.$services.employee
      .updateEmployee(payload)
      .then((employee: IEmployeeData) => {
        const index = context.state.employees.findIndex(
          (v: IEmployeeData) => v.id === employee.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE', employee);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_EMPLOYEE', employee);
        }
      });
  },

  deleteEmployee(context: EmployeeContext, payload: number): Promise<void> {
    return this.$services.employee.deleteEmployee(payload).then(() => {
      context.commit('REMOVE_EMPLOYEE', payload);
      context.commit('DECREMENT_COUNT');
    });
  },

  exportEmployeesXLSX(
    _context: EmployeeContext,
    payload: number
  ): Promise<Blob> {
    return this.$services.employee.exportEmployeesXLSX(payload);
  },

  uploadHourlyEmployees(
    _context: EmployeeContext,
    payload: File
  ): Promise<void> {
    return this.$services.employee.uploadHourlyEmployees(payload);
  },

  // Work Schedule
  fetchEmployeeWorkSchedule(
    context: EmployeeContext,
    payload: number
  ): Promise<void> {
    return this.$services.employee
      .fetchEmployeeWorkSchedule(payload)
      .then((employee: IEmployeeWorkScheduleData) => {
        context.commit('SET_EMPLOYEE_WORK_SCHEDULE', employee);
      });
  },

  createEmployeeWorkSchedule(
    context: EmployeeContext,
    payload: IEmployeeWorkScheduleData
  ): Promise<void> {
    return this.$services.employee
      .createEmployeeWorkSchedule(payload)
      .then((employee: IEmployeeWorkScheduleData) => {
        context.commit('SET_EMPLOYEE_WORK_SCHEDULE', employee);
      });
  },

  updateEmployeeWorkSchedule(
    context: EmployeeContext,
    payload: IEmployeeWorkScheduleData
  ): Promise<void> {
    return this.$services.employee
      .updateEmployeeWorkSchedule(payload)
      .then((employee: IEmployeeWorkScheduleData) => {
        context.commit('SET_EMPLOYEE_WORK_SCHEDULE', employee);
      });
  },

  deleteEmployeeWorkSchedule(
    context: EmployeeContext,
    payload: number
  ): Promise<void> {
    return this.$services.employee
      .deleteEmployeeWorkSchedule(payload)
      .then(() => {
        context.commit('REMOVE_EMPLOYEE_WORK_SCHEDULE', payload);
      });
  },

  fetchEmployeeTeamDepartments(
    context: EmployeeContext,
    payload: number
  ): Promise<void> {
    return this.$services.employee
      .fetchEmployeeTeamDepartments(payload)
      .then((hrDepartments: IHrDepartmentData[]) => {
        context.commit('SET_EMPLOYEE_TEAM_DEPARTMENTS', hrDepartments);
      });
  }
};
